import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckCircle, CircleNotch, Copy, DownloadSimple, Key, ShieldCheck, Warning, X,
} from '@phosphor-icons/react';

import {
  beginMfaSetup, confirmMfaSetup, disableMfa, fetchMfaStatus, regenerateBackupCodes,
  type MfaSetupBegin, type MfaStatus,
} from '@/lib/mfaApi';
import { getApiErrorMessage } from '@/lib/api';
import { Panel } from '@/features/settings/sections/primitives';

/**
 * Two-factor authentication — enrolment and management.
 *
 * ── THE RULE THIS SCREEN IS BUILT AROUND ──────────────────────────────────────────────────────
 * A barrister runs their whole practice on this account. Two-factor must never become the reason
 * they cannot get into it. So enrolment does not finish until the recovery codes have been put
 * somewhere — the "Done" button is disabled behind an explicit acknowledgement — and every
 * management action accepts a recovery code as well as the authenticator, because the case that
 * matters is the one where the phone is gone.
 *
 * ── WHAT IS NOT DONE HERE ─────────────────────────────────────────────────────────────────────
 * No code is validated, no secret is derived and no factor is judged satisfied in the browser.
 * The server owns all of that (apps/api/src/lib/mfa.ts). The length checks below only stop an
 * empty submit.
 *
 * ── SECRETS ───────────────────────────────────────────────────────────────────────────────────
 * The TOTP secret, the QR image (which encodes it) and the recovery codes are rendered and then
 * dropped with the component. None of them is logged, stored, or put in a URL. The server shows
 * recovery codes exactly once — only their SHA-256 hashes are kept — so "Regenerate" replaces the
 * set rather than re-reading it.
 */

const QUERY_KEY = ['mfa', 'status'] as const;

/** Enough to stop an empty submit. The server decides what is actually valid. */
const looksLikeTotp = (v: string) => /^\d{6}$/.test(v.replace(/\s/g, ''));
const looksLikeRecovery = (v: string) => v.replace(/[\s-]/g, '').length >= 16;

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ── Recovery codes, shown once ────────────────────────────────────────────────

function RecoveryCodeList({ codes }: { codes: string[] }) {
  async function copyAll() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success('Recovery codes copied');
    } catch {
      toast.error('Could not copy — select the codes and copy them manually.');
    }
  }

  function download() {
    // A Blob built in the page, not a server round trip: the codes must not travel anywhere they
    // have not already been, and the server could not re-send them anyway.
    const body = [
      'Jurivoxa — two-factor recovery codes',
      'Each code can be used once, in place of your authenticator app.',
      'Keep these somewhere only you can reach.',
      '',
      ...codes,
    ].join('\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'jurivoxa-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <ul className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/40 p-3 font-mono text-[13px] text-foreground sm:grid-cols-2">
        {codes.map((c) => (
          <li key={c} className="select-all tracking-wider">{c}</li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void copyAll()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted"
        >
          <Copy className="h-3.5 w-3.5" /> Copy all
        </button>
        <button
          type="button"
          onClick={download}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted"
        >
          <DownloadSimple className="h-3.5 w-3.5" /> Download
        </button>
      </div>
    </div>
  );
}

// ── A code prompt that accepts either factor ─────────────────────────────────

/**
 * Used by "turn off" and "regenerate". Both need proof of the second factor, and both must work
 * for someone whose authenticator is gone — otherwise losing the phone means the account is stuck
 * on two-factor forever, burning one recovery code per sign-in until there are none left.
 */
function SecondFactorPrompt({
  title,
  description,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
  pending,
  error,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: (code: string, useRecoveryCode: boolean) => void;
  pending: boolean;
  error: string | null;
}) {
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const ready = useRecovery ? looksLikeRecovery(code) : looksLikeTotp(code);

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>

      <div aria-live="polite">
        {error && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </p>
        )}
      </div>

      <div className="mt-3 max-w-xs">
        <label htmlFor="mfa-confirm-code" className="mb-1.5 block text-xs font-medium text-foreground">
          {useRecovery ? 'Recovery code' : 'Code from your authenticator app'}
        </label>
        <input
          id="mfa-confirm-code"
          type="text"
          autoComplete="one-time-code"
          inputMode={useRecovery ? 'text' : 'numeric'}
          spellCheck={false}
          autoFocus
          value={code}
          onChange={(e) =>
            setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))
          }
          placeholder={useRecovery ? 'XXXX-XXXX-XXXX-XXXX' : '000000'}
          className="h-10 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/50 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!ready || pending}
          onClick={() => onConfirm(code, useRecovery)}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground
            ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-brand-600 hover:bg-brand-700'}`}
        >
          {pending && <CircleNotch className="h-3.5 w-3.5 animate-spin" />}
          {confirmLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border px-3.5 py-2 text-xs font-medium text-foreground transition hover:bg-muted"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => { setUseRecovery((v) => !v); setCode(''); }}
          className="text-xs font-medium text-brand-600 transition hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
        >
          {useRecovery ? 'Use your authenticator app' : 'Use a recovery code instead'}
        </button>
      </div>
    </div>
  );
}

// ── Enrolment ─────────────────────────────────────────────────────────────────

function SetupFlow({ onCancel, onEnabled }: { onCancel: () => void; onEnabled: () => void }) {
  const [setup, setSetup] = useState<MfaSetupBegin | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Start enrolment ONCE, and let every effect pass read the same answer.
   *
   * ── WHY THE PROMISE IS MEMOISED RATHER THAN THE CALL GUARDED ──────────────────────────────
   * Two obvious versions of this are both wrong, and a real browser found them:
   *
   *   · `begin.mutate()` in the render body — the mutation is fired before React Query has
   *     subscribed the observer, so `onSuccess` never arrives and the panel sits on
   *     "Preparing your setup code…" forever.
   *   · a `useRef` boolean guard inside `useEffect` — StrictMode runs the effect, cleans up,
   *     and runs it again. The first pass sets the flag and owns the in-flight request; the
   *     second pass returns early; the first pass's state update is discarded with its
   *     cleanup. Same stuck spinner.
   *
   * Holding the PROMISE means exactly one request is ever sent — a second would mint a fresh
   * secret and silently invalidate a QR code the person may already have scanned — while both
   * effect passes await it, so the surviving one sets the state.
   */
  const request = useRef<Promise<MfaSetupBegin> | null>(null);

  useEffect(() => {
    request.current ??= beginMfaSetup();
    let alive = true;
    request.current
      .then((s) => { if (alive) setSetup(s); })
      .catch((e) => { if (alive) setError(getApiErrorMessage(e, 'Could not start two-factor setup')); });
    return () => { alive = false; };
  }, []);

  const confirm = useMutation({
    mutationFn: () => confirmMfaSetup(code),
    onSuccess: (backupCodes) => { setCodes(backupCodes); setError(null); },
    onError: (e) => { setError(getApiErrorMessage(e, 'That code was not accepted')); setCode(''); },
  });

  // ── Step 3: the codes, shown once ──
  if (codes) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" weight="fill" />
          <div>
            <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">Two-factor authentication is on</p>
            <p className="mt-0.5 text-xs text-emerald-800 dark:text-emerald-300">
              You will be asked for a code from your authenticator app each time you sign in.
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-foreground">Save your recovery codes now</h4>
          <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
            This is the only time they are shown — only a hash of each is kept, so they cannot be
            looked up again. If you lose your phone, these are how you get back in.
          </p>
          <RecoveryCodeList codes={codes} />
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 text-sm text-foreground">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-100"
          />
          <span>I have saved these codes somewhere only I can reach.</span>
        </label>

        <button
          type="button"
          disabled={!saved}
          onClick={onEnabled}
          className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
        >
          Done
        </button>
      </div>
    );
  }

  // ── Steps 1–2: scan, then confirm ──
  return (
    <div className="space-y-4">
      <div aria-live="polite">
        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </p>
        )}
      </div>

      {!setup ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" /> Preparing your setup code…
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            {/*
              White background is fixed, not themed: a QR code needs light quiet-zone contrast to
              scan, and in dark mode a transparent PNG on a dark card is unreadable by a camera.
            */}
            <div className="shrink-0 rounded-lg border border-border bg-white p-3">
              <img
                src={setup.qrCodeDataUrl}
                alt="QR code for setting up two-factor authentication"
                width={168}
                height={168}
                className="h-[168px] w-[168px]"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-foreground">1. Scan this with your authenticator app</h4>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Google Authenticator, 1Password, Authy, Microsoft Authenticator — any app that
                supports standard 6-digit codes.
              </p>
              <p className="mt-3 text-xs font-medium text-foreground">Can’t scan it?</p>
              <p className="mt-0.5 text-xs text-muted-foreground">Enter this key by hand instead:</p>
              <code className="mt-1.5 block select-all break-all rounded-md border border-border bg-muted/50 px-2.5 py-2 font-mono text-[12px] tracking-wider text-foreground">
                {setup.secret}
              </code>
            </div>
          </div>

          <div className="max-w-xs">
            <label htmlFor="mfa-setup-code" className="mb-1.5 block text-sm font-medium text-foreground">
              2. Enter the 6-digit code it shows
            </label>
            <input
              id="mfa-setup-code"
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              spellCheck={false}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-center font-mono text-lg tracking-[0.35em] text-foreground placeholder:text-muted-foreground/40 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!looksLikeTotp(code) || confirm.isPending}
              onClick={() => confirm.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
            >
              {confirm.isPending && <CircleNotch className="h-3.5 w-3.5 animate-spin" />}
              Turn on two-factor
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-border px-4 py-2 text-xs font-medium text-foreground transition hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── The panel ─────────────────────────────────────────────────────────────────

type Prompt = 'none' | 'disable' | 'regenerate';

export function MfaPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<MfaStatus>({ queryKey: QUERY_KEY, queryFn: fetchMfaStatus });
  const [enrolling, setEnrolling] = useState(false);
  const [prompt, setPrompt] = useState<Prompt>('none');
  const [promptError, setPromptError] = useState<string | null>(null);
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  const turnOff = useMutation({
    mutationFn: ({ code, useRecoveryCode }: { code: string; useRecoveryCode: boolean }) =>
      disableMfa(code, useRecoveryCode),
    onSuccess: () => {
      setPrompt('none'); setPromptError(null); setFreshCodes(null);
      void refresh();
      toast.success('Two-factor authentication turned off');
    },
    onError: (e) => setPromptError(getApiErrorMessage(e, 'Could not turn off two-factor')),
  });

  const regenerate = useMutation({
    mutationFn: ({ code, useRecoveryCode }: { code: string; useRecoveryCode: boolean }) =>
      regenerateBackupCodes(code, useRecoveryCode),
    onSuccess: (codes) => {
      setPrompt('none'); setPromptError(null); setFreshCodes(codes);
      void refresh();
    },
    onError: (e) => setPromptError(getApiErrorMessage(e, 'Could not replace your recovery codes')),
  });

  if (isLoading) {
    return (
      <Panel title="Two-factor authentication" description="A second step when you sign in">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" /> Loading…
        </p>
      </Panel>
    );
  }

  const enabled = data?.mfaEnabled ?? false;
  const remaining = data?.remainingBackupCodes ?? 0;

  // ── Off ──
  if (!enabled) {
    return (
      <Panel title="Two-factor authentication" description="A second step when you sign in">
        {enrolling ? (
          <SetupFlow
            onCancel={() => setEnrolling(false)}
            onEnabled={() => { setEnrolling(false); void refresh(); }}
          />
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                {/* The panel description above already states what two-factor does and why.
                    Any further explanation here only repeats it two lines later. */}
                <p className="text-sm font-medium text-foreground">Two-factor is off</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEnrolling(true)}
              className="shrink-0 rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-700"
            >
              Set up two-factor
            </button>
          </div>
        )}
      </Panel>
    );
  }

  // ── On ──
  return (
    <Panel
      title="Two-factor authentication"
      description="A second step when you sign in"
      action={
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
          <CheckCircle className="h-3.5 w-3.5" weight="fill" /> On
        </span>
      }
    >
      <div className="space-y-4">
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">Turned on</dt>
            <dd className="mt-0.5 text-sm text-foreground">{fmtDate(data?.verifiedAt ?? null)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Recovery codes left</dt>
            <dd className="mt-0.5 text-sm text-foreground">{remaining} of 8</dd>
          </div>
        </dl>

        {/*
          Not a silent number. Running out of recovery codes while the authenticator still works is
          survivable; running out after losing the phone is not, and by then it is too late to say so.
        */}
        {remaining <= 2 && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 dark:border-amber-500/30 dark:bg-amber-500/10">
            <Warning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              {remaining === 0
                ? 'You have no recovery codes left. If you lose your authenticator now you will not be able to sign in — replace them today.'
                : `Only ${remaining} recovery code${remaining === 1 ? '' : 's'} left. Replace them while you still have your authenticator.`}
            </p>
          </div>
        )}

        {freshCodes && (
          <div className="rounded-lg border border-border p-4">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Your new recovery codes</h4>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Your previous codes stopped working. These are shown once.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFreshCodes(null)}
                aria-label="Hide recovery codes"
                className="rounded p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <RecoveryCodeList codes={freshCodes} />
          </div>
        )}

        {prompt === 'regenerate' && (
          <SecondFactorPrompt
            title="Replace your recovery codes"
            description="The eight you have now will stop working immediately."
            confirmLabel="Replace codes"
            pending={regenerate.isPending}
            error={promptError}
            onCancel={() => { setPrompt('none'); setPromptError(null); }}
            onConfirm={(code, useRecoveryCode) => regenerate.mutate({ code, useRecoveryCode })}
          />
        )}

        {prompt === 'disable' && (
          <SecondFactorPrompt
            title="Turn off two-factor authentication"
            description="Your password alone will open your account again, and your recovery codes will be deleted."
            confirmLabel="Turn off two-factor"
            danger
            pending={turnOff.isPending}
            error={promptError}
            onCancel={() => { setPrompt('none'); setPromptError(null); }}
            onConfirm={(code, useRecoveryCode) => turnOff.mutate({ code, useRecoveryCode })}
          />
        )}

        {prompt === 'none' && (
          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={() => { setPrompt('regenerate'); setPromptError(null); }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3.5 py-2 text-xs font-medium text-foreground transition hover:bg-muted"
            >
              <Key className="h-3.5 w-3.5" /> Replace recovery codes
            </button>
            <button
              type="button"
              onClick={() => { setPrompt('disable'); setPromptError(null); }}
              className="rounded-lg border border-border px-3.5 py-2 text-xs font-medium text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              Turn off two-factor
            </button>
          </div>
        )}
      </div>
    </Panel>
  );
}
